<script lang="ts">
  import { _, t } from "svelte-i18n";
  import { goto } from "$app/navigation";

  import Typography from "$lib/components/Typography/Typography.svelte";
  import Button from "$lib/components/Button/Button.svelte";

  import TableSwitcher from "$lib/components/Switcher/Switcher.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import ClothingTypeDropdown from "./ClothingTypeDropdown/ClothingTypeDropdown.svelte";

  type Group = {
    title: string;
    subTypes?: string[];
  };

  type ClothingGroup = {
    gender: string;
    types: Group[];
  };
  export let clothingGroups: ClothingGroup[];
  export let selectedGender: ClothingGroup;
  export let selectedType: string;

  export let unit = "cm";

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;

  const onGenderClick = (clickedType: ClothingGroup) => {
    selectedGender = clickedType;
    if (selectedGender.types.length < 0) {
      selectedType = "";
    } else if (
      !selectedGender.types.some(type => type.title === selectedType)
    ) {
      selectedType = selectedGender.types[0].title;
    }
    let url = new URL(window.location.href);
    url.searchParams.set("gender", selectedGender?.gender);
    goto(url.href, { replaceState: true, noScroll: true });
  };

  const onTypeClick = (type: string) => {
    selectedType = type;
    let url = new URL(window.location.href);
    url.searchParams.set("type", type);
    goto(url.href, { replaceState: true, noScroll: true });
  };

  $: isMobile = viewportWidth < mdWidth;

  export let showFilterOverlay: boolean = false;

  const toggleFilterOverlay = () => {
    showFilterOverlay = !showFilterOverlay;
    if (showFilterOverlay) {
      document.querySelector("main").classList.add("filtered");
      window.document.body.classList.add("locked");
    } else {
      document.querySelector("main").classList.remove("filtered");
      window.document.body.classList.remove("locked");
    }
  };
</script>

<style lang="scss">
  .clothing-type-switcher {
    &--gender-btns {
      display: flex;
      margin-bottom: 48px;

      gap: 16px;
    }

    &--types {
      cursor: pointer;
      overflow-x: auto;
      white-space: nowrap;
      &::-webkit-scrollbar {
        display: none;
      }
      scrollbar-width: none;
      -ms-overflow-style: none;
    }

    &--bar {
      display: flex;
      justify-content: space-between;
      background-color: $on-surface;
      height: 48px;
      align-items: center;
    }

    &--type {
      background: none;
      margin: 0;
      padding: 0 10px;
      @include breakpoint(lg) {
        padding: 0 20px;
      }
      border: none;
      border-right: 1px solid $inverse-on-surface;
      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        border-right: none;
      }
    }

    &--type-dropdown {
      position: absolute;
    }

    &--filter-container {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    &--filter-icon {
      min-width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-right: 1px solid $inverse-on-surface-20;

      @include breakpoint(md) {
        display: none;
      }

      &__button {
        border: none;
        background-color: transparent;
      }
    }
  }

  @include breakpoint(lg) {
    .clothing-type-switcher {
      &--gender-btns {
        margin-bottom: 56px;
      }
    }
  }

  :global(.clothing-type-switcher--types-container) {
    max-width: 80% !important;
  }
  :global(.clothing-type-switcher--units-container) {
    @include breakpoint(md) {
      max-width: 20% !important;
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="clothing-type-switcher">
  <Container>
    <div class="clothing-type-switcher--gender-btns">
      {#each clothingGroups as clothingGroup}
        <Button
          on:click={() => onGenderClick(clothingGroup)}
          label={$_(clothingGroup.gender)}
          primaryInvert
          theme={selectedGender.gender === clothingGroup.gender
            ? "inverse-on-surface"
            : "on-surface"}
          hoverTheme={selectedGender.gender === clothingGroup.gender
            ? "on-surface"
            : "inverse-on-surface"}
          backgroundColor={selectedGender.gender === clothingGroup.gender
            ? "var(--on-surface)"
            : "var(--inverse-on-surface)"}
          hoverBgColor={selectedGender.gender === clothingGroup.gender
            ? "var(--inverse-on-surface)"
            : "var(--on-surface)"}
        />
      {/each}
    </div></Container
  >

  {#if selectedGender}
    <div class="clothing-type-switcher--bar">
      {#if !isMobile}
        <Container class="clothing-type-switcher--types-container">
          <div class="clothing-type-switcher--types">
            {#each selectedGender.types as type, index}
              {#if type.subTypes}
                <span
                  class="clothing-type-switcher--type clothing-type-switcher--type-dropdown"
                >
                  <ClothingTypeDropdown
                    title={type.title}
                    variant="button1"
                    items={type.subTypes}
                    bind:selection={selectedType}
                    light={true}
                  /></span
                >
              {:else}
                <span
                  role="button"
                  tabindex="0"
                  on:click={() => onTypeClick(type.title)}
                  on:keydown={() => onTypeClick(type.title)}
                  class="clothing-type-switcher--type {selectedType ===
                  type.title
                    ? 'clothing-type-switcher--type__active'
                    : ''}"
                >
                  <Typography
                    component="span"
                    variant="button1"
                    theme={selectedType === type.title
                      ? "inverse-on-surface-60"
                      : "inverse-on-surface"}
                  >
                    {type.title}
                  </Typography>
                </span>
              {/if}
            {/each}
          </div>
        </Container>
      {:else}
        <div class="clothing-type-switcher--filter-container">
          <div class="clothing-type-switcher--filter-icon">
            <button
              class="clothing-type-switcher--filter-icon__button"
              on:click={() => {
                toggleFilterOverlay();
              }}
            >
              <Icon icon="tune" color="var(--inverse-on-surface)" />
            </button>
          </div>
          <span
            role="button"
            tabindex="0"
            on:click={() => {
              toggleFilterOverlay();
            }}
            on:keydown={() => {
              toggleFilterOverlay();
            }}
          >
            <Typography
              component="span"
              variant="button1"
              theme={"inverse-on-surface"}
            >
              {$_("category")}
            </Typography>
          </span>
        </div>
      {/if}
      {#if selectedType !== "footwear"}
        <Container class="clothing-type-switcher--units-container">
          <TableSwitcher
            bind:selectedSwitchOption={unit}
            primary="cm"
            secondary="inch"
            dark={false}
          /></Container
        >{/if}
    </div>
  {/if}
</div>
